import type {CSSProperties} from 'react';
import React, {useRef} from 'react';

import CSSModules from 'react-css-modules';

import Button from '../../Button';
import FullScreenImageButton from '../../FullScreenImageButton';
import {
  combineStatuses,
  pending,
  rejected,
  resolved,
  usePreloadImage,
} from '../../hooks/preloadImage';
import {RetryIcon} from '../../icons';
import LoadingIndicator from '../../LoadingIndicator';
import {createMarkup, renderMarkdown} from '../../utils/markdownUtils';

import styles from './styles.module.scss';

type ImageProps = {
  //you (probably) shouldn't use this prop, and should instead use `this.image.src` (uses placeholder etc)
  imageUrl?: string;
  thumbnailImageUrl?: string;
  caption?: string;
  width?: number;
  height?: number;
  captionPlaceholder?: string;
  retryText: string;
  hovered?: boolean;
  showFullscreenButton?: boolean;
  reskinClassName?: string;
};

// name is different so we can use the JS Image() object
// eslint-disable-next-line complexity
const ImageBubble = ({
  imageUrl = '/resources/img/default_course_image.png',
  thumbnailImageUrl,
  hovered,
  caption,
  captionPlaceholder,
  retryText,
  showFullscreenButton = true,
  reskinClassName = '',
  width,
  height,
}: ImageProps): JSX.Element => {
  const thumnailImage = usePreloadImage(thumbnailImageUrl || imageUrl);
  const fullImage = usePreloadImage(imageUrl);
  const thumbnailImageRef = useRef<HTMLDivElement | null>(null);

  // eslint-disable-next-line complexity
  const calculateThumbnailHeight = () => {
    const minHeight = 182;
    const calculatedWidth =
      thumnailImage.status === resolved ? thumnailImage.width : width;
    const calculatedHeight =
      thumnailImage.status === resolved ? thumnailImage.height : height;

    let containerWidth =
      thumbnailImageRef.current?.getBoundingClientRect().width;

    if (hovered && containerWidth) {
      containerWidth -= 2;
    }
    const scaleFactor =
      containerWidth && calculatedWidth ? containerWidth / calculatedWidth : 1;

    if (
      calculatedWidth &&
      calculatedHeight &&
      calculatedWidth > calculatedHeight
    ) {
      //landscape; image should be scaled down to fit in as much width as possible, with min height 182px
      const idealHeight = scaleFactor * calculatedHeight;
      return Math.max(minHeight, idealHeight);
    } else {
      //square or portrait; image should be shown as square, and top/bottom cropped
      return containerWidth;
    }
  };

  const getThumbnailContents = () => {
    switch (combineStatuses(thumnailImage.status, fullImage.status)) {
      case rejected:
        return (
          <div styleName='noInternetOverlay'>
            <div styleName='noInternetContent'>
              <Button
                type='thirdary'
                onClick={() => {
                  thumnailImage.reload();
                  fullImage.reload();
                }}
                icon={<RetryIcon />}
                noMarginTop
                noMarginLeft
              >
                {retryText}
              </Button>
            </div>
          </div>
        );
      case pending:
        return (
          <LoadingIndicator indicator='generic' size='normal' color='dark' />
        );
      case resolved:
        return (
          showFullscreenButton &&
          imageUrl && <FullScreenImageButton image={fullImage} />
        );
      default:
        throw Error('invalid image state');
    }
  };

  const imgPositionerStyle = 'imgPositioner' + (hovered ? ' hovered' : '');
  const backgroundImage = `url(${thumnailImage.src})`;

  const showCaption = caption || captionPlaceholder;

  const previewHeight = thumbnailImageRef.current
    ? calculateThumbnailHeight()
    : 211;

  const thumbnailStyles: CSSProperties = {
    backgroundImage: thumnailImage.status === resolved ? backgroundImage : '',
    height: previewHeight,
  };

  return (
    <div styleName={reskinClassName}>
      <div
        styleName={imgPositionerStyle}
        style={thumbnailStyles}
        ref={thumbnailImageRef}
        data-automation='component-image'
      >
        {getThumbnailContents()}
      </div>
      {showCaption && (
        <div
          styleName={`captionBody ${!caption ? 'grey' : ''}`}
          data-automation='component-description'
        >
          <div
            dangerouslySetInnerHTML={createMarkup(
              renderMarkdown(caption || captionPlaceholder || '')
            )}
          />
        </div>
      )}
    </div>
  );
};

export default CSSModules(ImageBubble, styles, {allowMultiple: true});
